/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import * as AppPropTypes from '../../lib/PropTypes';
import { getImageSize } from '../../lib/utils';

import styles from '../../../styles/partials/image.module.scss';
import { useWindowSize } from '../../contexts/SiteContext';

const propTypes = {
    media: AppPropTypes.image,
    size: PropTypes.string,
    alt: PropTypes.string,
    loading: PropTypes.string,
    loadingClassName: PropTypes.string,
    fit: PropTypes.string,
    style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    onLoad: PropTypes.func,
    ratio: PropTypes.number,
    className: PropTypes.string,
};

const defaultProps = {
    media: null,
    size: 'large',
    alt: null,
    loading: 'lazy',
    loadingClassName: null,
    fit: null,
    style: null,
    onLoad: null,
    ratio: null,
    className: null,
};

const Image = ({
    media,
    size,
    alt,
    loading,
    loadingClassName,
    fit,
    style,
    onLoad,
    ratio,
    className,
    ...props
}) => {
    const { width: windowWidth } = useWindowSize();
    const imageRef = useRef();
    const imageSize = getImageSize(media, size);
    const {
        url: originalUrl = null,
        width: mediaWidth = null,
        height: mediaHeight = null,
    } = media || {};
    const { url = null, width = mediaWidth, height = mediaHeight } = imageSize || {};

    const [isLoading, setIsLoading] = useState(true);
    const [useOriginalUrl, setUseOriginalUrl] = useState(false);

    const finalUrl = useOriginalUrl ? originalUrl : url;

    useEffect(() => {
        if (finalUrl !== null) {
            const image = document.createElement('img');
            image.onload = () => {
                setIsLoading(false);
            };
            image.onerror = () => {
                setUseOriginalUrl(true);
            };
            image.src = finalUrl;
        }
    }, [finalUrl]);

    const fitStyle = useMemo(
        () => ({
            width: fit === 'cover' ? '100%' : null,
            height: fit === 'cover' ? '100%' : null,
        }),
        [fit],
    );

    const ratioHeight = useMemo(() => {
        if (imageRef.current && ratio !== null) {
            const rect = imageRef.current.getBoundingClientRect();
            return rect.width ? rect.width * ratio : null;
        }
        return null;
    }, [mediaWidth, ratio, imageRef.current, windowWidth]);

    return ratio !== null ? (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.maintainRatio]: ratio !== null,
                    [loadingClassName]: loadingClassName !== null && isLoading,
                    [className]: className !== null,
                },
            ])}
            ref={imageRef}
            style={{
                width: '100%',
                height: ratioHeight,
                // paddingBottom: `56.25%`,
                // paddingBottom: `${ratio * 100}%`,
                backgroundImage: `url(${url})`,
            }}
        />
    ) : (
        <img
            className={classNames([
                styles.container,
                {
                    [loadingClassName]: loadingClassName !== null && isLoading,
                    [className]: className !== null,
                },
            ])}
            src={finalUrl}
            alt={alt}
            width={width !== null && width > 0 ? width : null}
            height={height !== null && height > 0 ? height : null}
            loading={loading}
            draggable="false"
            onLoad={onLoad}
            ref={imageRef}
            style={{ objectFit: fit, ...fitStyle, ...style }}
            {...props}
        />
    );
};

Image.propTypes = propTypes;
Image.defaultProps = defaultProps;

export default Image;
